import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import './Dashboard.css';

function Dashboard() {
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [viewingSession, setViewingSession] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  const loadSessions = () => {
    // Load sessions from localStorage
    const savedSessions = localStorage.getItem('clinical_sessions');
    if (savedSessions) {
      try {
        setSessions(JSON.parse(savedSessions));
      } catch (e) {
        setSessions([]);
      }
    } else {
      setSessions([]);
    }
  };

  const handleDelete = (sessionId) => {
    if (window.confirm('Are you sure you want to delete this conversation?')) {
      const updatedSessions = sessions.filter(s => s.id !== sessionId);
      setSessions(updatedSessions);
      localStorage.setItem('clinical_sessions', JSON.stringify(updatedSessions));
    }
  };

  useEffect(() => {
    // Load current user
    const storedUser = localStorage.getItem('user');
    if (storedUser) {
      try {
        setCurrentUser(JSON.parse(storedUser));
      } catch (e) {
        setCurrentUser(null);
      }
    }
    
    loadSessions();
    setLoading(false);
  }, []);

  const getStatusBadge = (status) => {
    const badges = {
      'pending': { class: 'badge-pending', text: 'Pending' },
      'recorded': { class: 'badge-warning', text: 'Needs Review' },
      'completed': { class: 'badge-success', text: 'Completed' }
    };
    const badge = badges[status] || badges['pending'];
    return <span className={`badge ${badge.class}`}>{badge.text}</span>;
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="dashboard-container">
      <div className="dashboard-header">
        <h1>Recorded Conversations</h1>
        <Link to="/record-new" className="btn-primary">
          + New Recording
        </Link>
      </div>

      <div className="conversations-list">
        {loading ? (
          <div className="loading">Loading conversations...</div>
        ) : sessions.length === 0 ? (
          <div className="empty-state">
            <p>No conversations yet. Start by recording your first clinical conversation.</p>
            <Link to="/record-new" className="btn-primary">Start Recording</Link>
          </div>
        ) : (
          <div className="conversation-cards">
            {sessions.map((session) => (
              <div 
                key={session.id} 
                className="conversation-card"
                onClick={() => setViewingSession(session)}
                style={{ cursor: 'pointer' }}
              >
                <div className="card-content">
                  <div className="patient-info">
                    <h3>{session.patient_first_name} {session.patient_last_name}</h3>
                    <span className="session-date">{formatDate(session.created_at)}</span>
                  </div>
                  <div className="card-actions">
                    {getStatusBadge(session.status)}
                    <button 
                      className="delete-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(session.id);
                      }}
                      title="Delete conversation"
                    >
                      🗑️ Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {viewingSession && (
        <div className="modal-overlay" onClick={() => setViewingSession(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Conversation Details</h2>
              <button className="close-btn" onClick={() => setViewingSession(null)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="detail-section">
                <h3>Patient Information</h3>
                <p><strong>Name:</strong> {viewingSession.patient_first_name} {viewingSession.patient_last_name}</p>
                <p><strong>Date:</strong> {formatDate(viewingSession.created_at)}</p>
                <p><strong>Duration:</strong> {Math.floor(viewingSession.duration_seconds / 60)}m {viewingSession.duration_seconds % 60}s</p>
                <p><strong>Status:</strong> {getStatusBadge(viewingSession.status)}</p>
              </div>

              {viewingSession.transcript && currentUser?.role !== 'nurse' && (
                <div className="detail-section">
                  <h3>📝 Transcript</h3>
                  <div className="transcript-box">{viewingSession.transcript}</div>
                </div>
              )}

              {currentUser?.role === 'nurse' && (
                <div className="detail-section nurse-notice">
                  <h3>🔒 Access Restricted</h3>
                  <p>As a nurse, you can only view medical information and analysis, not the full conversation transcript.</p>
                </div>
              )}

              {viewingSession.analysis && (
                <div className="detail-section">
                  <h3>🤖 AI Analysis (SOAP Notes)</h3>
                  <div className="soap-section">
                    <h4>Chief Complaint</h4>
                    <p>{viewingSession.analysis.chiefComplaint}</p>
                  </div>
                  <div className="soap-section">
                    <h4>Subjective (S)</h4>
                    <p>{viewingSession.analysis.subjective}</p>
                  </div>
                  <div className="soap-section">
                    <h4>Objective (O)</h4>
                    <p>{viewingSession.analysis.objective}</p>
                  </div>
                  <div className="soap-section">
                    <h4>Assessment (A)</h4>
                    <p>{viewingSession.analysis.assessment}</p>
                  </div>
                  <div className="soap-section">
                    <h4>Plan (P)</h4>
                    <p>{viewingSession.analysis.plan}</p>
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setViewingSession(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
